/**
 * Delete Accounts Dialog
 *
 * Type-to-confirm dialog for permanently deleting one or more accounts.
 * Single delete requires the account name; bulk delete requires "DELETE".
 */
import { useEffect, useState } from 'react';
import { Button, Modal, Input, Label } from '@respectly/ui';
import { AlertTriangle } from 'lucide-react';

interface DeleteAccountsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  /** Total number of accounts being deleted */
  count: number;
  /** Accounts whose details are known (may be fewer than count for cross-page selections) */
  accounts: { id: string; name: string; status: string }[];
}

const MAX_LISTED = 8;

export function DeleteAccountsDialog({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
  count,
  accounts,
}: DeleteAccountsDialogProps) {
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (isOpen) setConfirmText('');
  }, [isOpen]);

  const single = count === 1 && accounts.length === 1 ? accounts[0] : undefined;
  const isSingle = !!single;
  const requiredText = single ? single.name : 'DELETE';
  const activeCount = accounts.filter((a) => a.status !== 'cancelled').length;
  const unlistedCount = count - Math.min(accounts.length, MAX_LISTED);
  const canConfirm = confirmText.trim() === requiredText && !isDeleting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isDeleting ? () => {} : onClose}
      title={isSingle ? 'Permanently Delete Account' : `Permanently Delete ${count} Accounts`}
    >
      <div className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">This cannot be undone.</p>
            <p>
              All users, contacts, campaigns, conversations, messages, documents, forms, and billing
              records will be erased. Any active Helcim subscription is cancelled and phone numbers
              are returned to the pool.
            </p>
          </div>
        </div>

        {activeCount > 0 && (
          <p className="text-sm font-medium text-amber-600 dark:text-amber-500">
            {single
              ? `This account is currently ${single.status}. Its users will lose access immediately.`
              : `${activeCount} of the selected accounts are not cancelled. Their users will lose access immediately.`}
          </p>
        )}

        {!isSingle && (
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-300">
            {accounts.slice(0, MAX_LISTED).map((a) => (
              <li key={a.id} className="truncate">
                {a.name} <span className="text-xs text-slate-400">({a.status})</span>
              </li>
            ))}
            {unlistedCount > 0 && (
              <li className="text-xs text-slate-400">and {unlistedCount} more</li>
            )}
          </ul>
        )}

        <div className="space-y-2">
          <Label htmlFor="delete-confirm">
            Type <span className="font-mono font-semibold">{requiredText}</span> to confirm
          </Label>
          <Input
            id="delete-confirm"
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canConfirm) onConfirm();
            }}
            disabled={isDeleting}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!canConfirm}>
            {isDeleting
              ? 'Deleting...'
              : isSingle
                ? 'Delete Permanently'
                : `Delete ${count} Accounts`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
